import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Stream } from "@cloudflare/stream-react";
import {
  Play,
  Shield,
  Clock,
  TrendingUp,
  ArrowRight,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  staggerContainer,
  fadeInUp,
  scaleIn,
  floatingAnimation,
  floatingTransition,
  floatingAnimationAlt,
  floatingTransitionAlt,
} from "@/lib/animations";

// ============================================================================
// VSL HERO SECTION - Video Sales Letter First Design
// Cinematic, premium presentation that puts the video front and center
// ============================================================================

// Cloudflare Stream Video ID
const DEMO_VIDEO_ID = "70ace7354ed810b179b2496be9b2af75";

const VSLHeroSection = ({
  onScrollToUpload,
  videoId = import.meta.env.VITE_CLOUDFLARE_STREAM_VIDEO_ID || DEMO_VIDEO_ID,
  autoplay = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [isMuted, setIsMuted] = useState(autoplay); // Autoplay requires muted
  const streamRef = useRef(null);

  // Handle play button click
  const handlePlay = () => {
    setIsPlaying(true);
    setIsMuted(false);
    // Small delay to ensure component is mounted
    setTimeout(() => {
      if (streamRef.current) {
        streamRef.current.play();
      }
    }, 100);
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (streamRef.current) {
      streamRef.current.muted = !isMuted;
    }
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden py-8 md:py-12">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800" />

      {/* Subtle radial gradient for depth */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/10 via-transparent to-transparent" />

      {/* Animated grain texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.015] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Floating ambient elements */}
      <motion.div
        className="absolute top-20 right-[10%] w-96 h-96 rounded-full bg-amber-500/5 blur-3xl"
        animate={floatingAnimation}
        transition={floatingTransition}
      />
      <motion.div
        className="absolute bottom-20 left-[5%] w-[500px] h-[500px] rounded-full bg-slate-400/5 blur-3xl"
        animate={floatingAnimationAlt}
        transition={floatingTransitionAlt}
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center"
        >
          {/* Badge */}
          <motion.div variants={fadeInUp} className="mb-4 md:mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-amber-400 text-sm font-medium backdrop-blur-sm">
              <Sparkles className="w-4 h-4" />
              See It In Action
            </span>
          </motion.div>

          {/* Headline - Short and punchy */}
          <motion.h1
            variants={fadeInUp}
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center leading-tight tracking-tight mb-6 md:mb-10"
          >
            Property Data in{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-300 to-orange-400">
              60 Seconds
            </span>
          </motion.h1>

          {/* Video Container */}
          <motion.div
            variants={scaleIn}
            className="relative w-full max-w-4xl mx-auto mb-8 md:mb-12"
          >
            {/* Gradient border glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-amber-500/20 rounded-2xl blur-xl opacity-60" />

            {/* Video frame */}
            <div className="relative bg-slate-950 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
              {/* Aspect ratio container - 16:9 */}
              <div className="relative aspect-video">

                {/* Play button overlay - shown before video starts */}
                <AnimatePresence>
                  {!isPlaying && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-gradient-to-b from-slate-900/80 via-slate-900/60 to-slate-900/80 backdrop-blur-sm cursor-pointer"
                      onClick={handlePlay}
                    >
                      {/* Decorative rings behind play button */}
                      <div className="absolute">
                        <motion.div
                          className="w-32 h-32 rounded-full border border-amber-500/20"
                          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        />
                      </div>
                      <div className="absolute">
                        <motion.div
                          className="w-48 h-48 rounded-full border border-amber-500/10"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.05, 0.2] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                        />
                      </div>

                      {/* Play button */}
                      <motion.button
                        className="relative z-10 flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 transition-shadow duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" fill="white" />
                      </motion.button>

                      {/* Text below button */}
                      <motion.p
                        className="mt-6 text-white/80 font-medium text-base md:text-lg"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        Watch How It Works
                      </motion.p>
                      <motion.p
                        className="mt-1 text-white/50 text-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                      >
                        1 minute video
                      </motion.p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Video thumbnail/poster (shown before play) */}
                {!isPlaying && (
                  <div className="absolute inset-0 z-10">
                    {/* Placeholder gradient - replace with actual thumbnail if available */}
                    <div className="w-full h-full bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950" />
                  </div>
                )}

                {/* Cloudflare Stream Player */}
                {videoId ? (
                  <div className={`absolute inset-0 ${!isPlaying ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500`}>
                    <Stream
                      streamRef={streamRef}
                      src={videoId}
                      controls={isPlaying}
                      muted={isMuted}
                      autoplay={isPlaying}
                      preload="metadata"
                      primaryColor="#f59e0b"
                      className="w-full h-full"
                    />
                  </div>
                ) : (
                  /* Fallback when no video ID */
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                    <div className="text-center text-white/50">
                      <p className="text-lg font-medium">Video Coming Soon</p>
                      <p className="text-sm mt-1">Configure VITE_CLOUDFLARE_STREAM_VIDEO_ID</p>
                    </div>
                  </div>
                )}

                {/* Mute toggle (shown when playing) */}
                <AnimatePresence>
                  {isPlaying && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      onClick={toggleMute}
                      className="absolute bottom-4 right-4 z-30 p-2 rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/70 transition-colors"
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5" />
                      ) : (
                        <Volume2 className="w-5 h-5" />
                      )}
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>

              {/* Decorative corner accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-amber-500/30 rounded-tl-2xl pointer-events-none" />
              <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-amber-500/30 rounded-tr-2xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-amber-500/30 rounded-bl-2xl pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-amber-500/30 rounded-br-2xl pointer-events-none" />
            </div>
          </motion.div>

          {/* Trust Bar + CTA */}
          <motion.div
            variants={fadeInUp}
            className="flex flex-col items-center gap-6"
          >
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span>Bank-level encryption</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/20" />
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                <span>Results in minutes</span>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/20" />
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <span>2,500+ professionals</span>
              </div>
            </div>

            {/* CTA Button */}
            <Button
              onClick={onScrollToUpload}
              className="group h-14 px-8 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white rounded-xl shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 border-0"
            >
              Upload Your File
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>

            <p className="text-white/40 text-sm">
              No account needed — just upload and go
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/20 flex justify-center pt-2">
          <motion.div
            className="w-1.5 h-1.5 rounded-full bg-white/40"
            animate={{ y: [0, 12, 0], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default VSLHeroSection;
